/**
 * La météo du jour, et le mot « prends la tenue ». Elle fait ses visites à vélo :
 * un emoji en tête de journée lui dit le temps qu'il fait, et une phrase courte lui
 * dit quand s'habiller en conséquence — jamais plus de trois, jamais sans raison.
 *
 * Tout est pur et se calcule des deux côtés : le serveur lit Open-Meteo, le client
 * en tire l'en-tête d'Aujourd'hui, les colonnes de Semaine et les conseils. Comme
 * partout dans l'app, les heures sont des chaînes Europe/Paris naïves.
 */
import { formatFrenchHour, minutesOfTime, timeOf } from './time.js';
/** La météo est allumée tant qu'elle ne l'éteint pas : c'est un décor utile. */
export const DEFAULT_METEO_ENABLED = true;
/**
 * Les codes WMO, rangés du plus calme au plus rude. L'ordre décide de l'emoji d'une
 * journée entière : le pire l'emporte — une heure d'orage compte plus que six heures
 * de soleil, parce que c'est elle qui change ce qu'on met dans la sacoche.
 */
const WEATHER_KINDS = [
    { emoji: '☀️', codes: [0] },
    { emoji: '🌤️', codes: [1, 2] },
    { emoji: '☁️', codes: [3] },
    { emoji: '🌫️', codes: [45, 48] },
    { emoji: '🌦️', codes: [51, 53, 55, 56, 57] },
    { emoji: '🌨️', codes: [71, 73, 75, 77, 85, 86] },
    { emoji: '🌧️', codes: [61, 63, 65, 66, 67, 80, 81, 82] },
    { emoji: '⛈️', codes: [95, 96, 97, 98, 99] },
];
/** Le rang d'un code dans l'échelle ci-dessus ; -1 pour un code inconnu. */
function severity(code) {
    return WEATHER_KINDS.findIndex((kind) => kind.codes.includes(code));
}
/** À partir d'ici, le temps se voit sur la route : neige, pluie, orage. */
const ROUGH_RANK = WEATHER_KINDS.findIndex((kind) => kind.emoji === '🌨️');
const STORM_RANK = WEATHER_KINDS.length - 1;
/** L'emoji d'un code temps WMO ; null quand le code ne dit rien de connu. */
export function weatherEmoji(code) {
    return WEATHER_KINDS[severity(code)]?.emoji ?? null;
}
/** « 22° » — une température comme elle se lit, sans décimale. */
export function formatDegrees(tempC) {
    return `${Math.round(tempC)}°`;
}
// -------------------------------------------------------------- les seuils
/** Une averse annoncée à une chance sur deux se prépare ; en dessous, on verra bien. */
const RAIN_PROBABILITY = 50;
/** Trois dixièmes de millimètre sur une heure : de quoi être mouillée, pas une bruine. */
const RAIN_MM = 0.3;
/** En dessous, les doigts sur le guidon gèlent au bout de dix minutes. */
const COLD_C = 5;
/** 40 km/h de vent, c'est un trajet qui change de nature. */
const WIND_KMH = 40;
const HEAT_C = 32;
const WIND_EMOJI = '💨';
/** Trois lignes : au-delà, ce n'est plus un conseil, c'est un bulletin. */
export const SUIT_UP_MAX = 3;
/** Un conseil, et l'heure qui le justifie — l'ordre du tableau est celui de l'urgence. */
const ADVICE = [
    {
        matches: (hour) => severity(hour.code) === STORM_RANK,
        say: (hour) => `⛈️ Orage prévu vers ${whenOf(hour)} : prudence.`,
    },
    {
        matches: (hour) => hour.precipProbability >= RAIN_PROBABILITY || hour.precipMm >= RAIN_MM,
        say: (hour) => `🌧️ Pluie prévue vers ${whenOf(hour)} : prends la tenue de pluie.`,
    },
    {
        matches: (hour) => hour.tempC <= COLD_C,
        say: (hour) => `🧤 ${formatDegrees(hour.tempC)} à ${whenOf(hour)} : gants et couche chaude.`,
    },
    {
        matches: (hour) => hour.windKmh >= WIND_KMH,
        say: (hour) => `${WIND_EMOJI} Vent fort vers ${whenOf(hour)} : ça pousse.`,
    },
    {
        matches: (hour) => hour.tempC >= HEAT_C,
        say: (hour) => `🥵 ${formatDegrees(hour.tempC)} à ${whenOf(hour)} : de l’eau et de l’ombre.`,
    },
];
/**
 * Ce qu'il faut prévoir pour les trajets de la journée, le plus important d'abord.
 * Seules les heures qu'elle passe sur le vélo comptent : une averse à 22 h ne la
 * concerne pas. Pas de trajet ce jour-là — une journée en visio, un dimanche — et il
 * n'y a rien à dire du tout.
 *
 * `hours` est chronologique : chaque conseil nomme la première heure qui le motive.
 */
export function suitUp(hours, windows) {
    if (windows.length === 0)
        return [];
    const riding = hours.filter((hour) => windows.some((window) => overlaps(hour, window)));
    return ADVICE.flatMap(({ matches, say }) => {
        const hour = riding.find(matches);
        return hour === undefined ? [] : [say(hour)];
    }).slice(0, SUIT_UP_MAX);
}
/**
 * L'emoji à poser au bout d'une ligne « Aller à … », quand l'heure mérite un mot :
 * la neige, la pluie, l'orage, ou un vent qui pousse. Rien par temps clément — une
 * belle journée laisse la timeline tranquille.
 */
export function rideEmoji(hour) {
    const rank = severity(hour.code);
    if (rank >= ROUGH_RANK)
        return weatherEmoji(hour.code);
    return hour.windKmh >= WIND_KMH ? WIND_EMOJI : null;
}
/** L'heure de prévision qui couvre un moment de la journée ; null hors fenêtre. */
export function hourAt(hours, at) {
    return hours.find((hour) => hour.at.slice(11, 13) === at.slice(0, 2)) ?? null;
}
/** Ses journées de travail ; en dehors, le temps qu'il fait ne la fait pas rouler. */
export const WORKING_FROM = '08:00';
export const WORKING_TO = '19:00';
/** L'heure la plus chaude de la journée : celle que l'en-tête cite. */
const HEADLINE_HOUR = '14';
/**
 * Ce que l'en-tête d'Aujourd'hui affiche : « ☀️ 22° ». Le pire temps de la tranche
 * donne l'emoji, la température est celle de 14 h — à défaut, la plus haute connue.
 * Borne de fin exclue : la journée s'arrête à 19 h.
 */
export function daySummary(hours, from = WORKING_FROM, to = WORKING_TO) {
    const inRange = hours.filter((hour) => {
        const time = timeOf(hour.at);
        return time >= from && time < to;
    });
    if (inRange.length === 0)
        return null;
    const worst = inRange.reduce((kept, hour) => severity(hour.code) > severity(kept.code) ? hour : kept);
    const emoji = weatherEmoji(worst.code);
    if (emoji === null)
        return null;
    const headline = inRange.find((hour) => hour.at.slice(11, 13) === HEADLINE_HOUR);
    const tempC = headline?.tempC ?? Math.max(...inRange.map((hour) => hour.tempC));
    return { emoji, tempC };
}
/** L'heure pleine qu'une phrase nomme : « vers 14 h », « à 8 h ». */
function whenOf(hour) {
    return formatFrenchHour(timeOf(hour.at));
}
/** Une heure de prévision couvre `[HH:00, HH+1:00)` ; le trajet, `[from, to)`. */
function overlaps(hour, window) {
    const start = minutesOfTime(timeOf(hour.at));
    return start < minutesOfTime(window.to) && start + 60 > minutesOfTime(window.from);
}
//# sourceMappingURL=meteo.js.map